import React from 'react';
import { formatCurrency } from '../../utils/formatters';

/**
 * Displays the merchant's balance information
 */
const BalanceCard = ({ 
  availableBalance, 
  pendingBalance, 
  instantAvailableBalance,
  platformPendingProductEarningsData,
  lastUpdated, 
  displayCurrency
}) => {
  // Use displayCurrency as the primary currency for general balances,
  // but allow platformPendingProductEarningsData to override for its specific section.
  const effectiveDisplayCurrency = displayCurrency || 'USD';
  const platformEarningsCurrency = platformPendingProductEarningsData?.currency || effectiveDisplayCurrency;
  
  // Log currency information
  React.useEffect(() => {
    if (platformPendingProductEarningsData) {
      console.log('Platform pending earnings data (BalanceCard):', platformPendingProductEarningsData);
    }
    console.log('BalanceCard effectiveDisplayCurrency:', effectiveDisplayCurrency);
  }, [platformPendingProductEarningsData, effectiveDisplayCurrency]);

  const earningsByCurrency = platformPendingProductEarningsData?.platformPendingEarningsByCurrency;

  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden border border-gray-200">
      <div className="p-5 bg-gradient-to-r from-teal-50 to-teal-100 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800">Balance Overview</h3>
        {lastUpdated && (
          <p className="text-xs text-gray-500 mt-1">Last updated: {lastUpdated}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200">
        {/* Stripe Available Balance */}
        <div className="p-5">
          <p className="text-sm font-medium text-gray-500">Available Balance</p>
          <p className="text-xl font-bold mt-1 text-gray-900">
            {typeof availableBalance === 'number' 
              ? formatCurrency(availableBalance, effectiveDisplayCurrency)
              : 'Connect Stripe to view'
            }
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Available for withdrawal
        </p>
      </div>

        {/* Stripe Pending Balance */}
        <div className="p-5">
          <p className="text-sm font-medium text-gray-500">Pending Balance</p>
          <p className="text-xl font-bold mt-1 text-gray-900">
            {typeof pendingBalance === 'number'
              ? formatCurrency(pendingBalance, effectiveDisplayCurrency)
              : 'Connect Stripe to view'
            }
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Processing in Stripe
          </p>
        </div>
        
        {/* Platform Pending Earnings by Currency */}
        <div className="p-5">
          <p className="text-sm font-medium text-gray-500">Pending Earnings (by Currency)</p>
          {earningsByCurrency && earningsByCurrency.length > 0 ? (
            earningsByCurrency.map((earning, index) => (
              <div key={index} className={index > 0 ? 'mt-3 pt-3 border-t border-gray-100' : 'mt-1'}>
                <p className="text-xl font-bold text-gray-900">
                  {formatCurrency(earning.amount, earning.currency.toUpperCase())}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {`From ${earning.numberOfOrders} pending order(s) in ${earning.currency.toUpperCase()}`}
                </p>
              </div>
            ))
          ) : (
            <>
              <p className="text-xl font-bold mt-1 text-gray-900">
                {formatCurrency(0, effectiveDisplayCurrency)}
              </p>
              <p className="text-xs text-gray-500 mt-1">No pending platform earnings</p>
            </>
          )}
          {platformPendingProductEarningsData?.totalNumberOfPendingOrders > 0 && (!earningsByCurrency || earningsByCurrency.length === 0) && (
             <p className="text-xs text-gray-500 mt-1">(Error: Orders found but no currency breakdown)</p> // Fallback message if data is inconsistent
          )}
        </div>
      </div>
    </div>
  );
};

export default BalanceCard; 